import type {
  RouteLocationRaw,
  RouteMeta,
  Router,
  RouteRecordRaw,
} from 'vue-router';

import {
  ADMIN_DASHBOARD_ROUTE_NAME,
  AFFILIATE_DASHBOARD_ROUTE_NAME,
} from '@modules/dashboard/constants';

import type {
  AppVueRouter,
  RouteConfig,
} from './types';
import {
  ADMIN_REDIRECT_ROUTE_NAME,
  ADMIN_ROUTE_NAME,
  AFFILIATE_REDIRECT_ROUTE_NAME,
  AFFILIATE_ROUTE_NAME,
  ANONYMOUS_ROUTE_NAME,
  SHARED_ROUTE_NAME,
} from './constants';
import { ERouteNamespace } from './enums';

function prepareConfig(config: RouteConfig): RouteRecordRaw {
  const { permission, keepAliveInclude, ...restConfig } = config;

  const meta: RouteMeta = {
    ...(config.meta ?? {}),
  };

  if (permission) {
    meta.permission = permission;
  }

  if (keepAliveInclude) {
    meta.keepAliveInclude = keepAliveInclude;
  }

  return {
    ...restConfig,
    meta,
  };
}

export default function getAppVueRouter(
  router: Router,
): AppVueRouter {
  router.addRoute({
    name: ADMIN_ROUTE_NAME,
    path: '/admin',
    component: () => import('@pages/user/admin/AdminPage.vue'),
    meta: { namespace: ERouteNamespace.ADMIN },
  });

  router.addRoute({
    name: AFFILIATE_ROUTE_NAME,
    path: '/affiliate',
    component: () => import('@pages/user/affiliate/AffiliatePage.vue'),
    meta: { namespace: ERouteNamespace.AFFILIATE },
  });

  router.addRoute({
    name: ANONYMOUS_ROUTE_NAME,
    path: '/anonymous',
    component: () => import('@pages/anonymous/AnonymousPage.vue'),
    meta: { namespace: ERouteNamespace.ANONYMOUS },
  });

  router.addRoute({
    name: SHARED_ROUTE_NAME,
    path: '/shared',
    component: () => import('@pages/shared/SharedPage.vue'),
    meta: { namespace: ERouteNamespace.ANY },
  });

  // Redirect routes
  router.addRoute(ADMIN_ROUTE_NAME, {
    name: ADMIN_REDIRECT_ROUTE_NAME,
    path: '',
    redirect: {
      name: ADMIN_DASHBOARD_ROUTE_NAME,
    },
  });

  router.addRoute(AFFILIATE_ROUTE_NAME, {
    name: AFFILIATE_REDIRECT_ROUTE_NAME,
    path: '',
    redirect: {
      name: AFFILIATE_DASHBOARD_ROUTE_NAME,
    },
  });

  return {
    ...router,
    addAdminRoute(config: RouteConfig): void {
      router.addRoute(ADMIN_ROUTE_NAME, prepareConfig(config));
    },
    addAffiliateRoute(config: RouteConfig): void {
      router.addRoute(AFFILIATE_ROUTE_NAME, prepareConfig(config));
    },
    addAnonymousRoute(config: RouteConfig): void {
      router.addRoute(ANONYMOUS_ROUTE_NAME, prepareConfig(config));
    },
    addSharedRoute(config: RouteConfig): void {
      router.addRoute(SHARED_ROUTE_NAME, prepareConfig(config));
    },
    addChildRoute(parentNode: RouteConfig['name'], config: RouteConfig): void {
      router.addRoute(parentNode, prepareConfig(config));
    },
    back(defaultLocation?: RouteLocationRaw): void {
      if (!window.history.state?.back) {
        void router.replace(defaultLocation || {
          path: '/',
        });
      } else {
        router.back();
      }
    },
  };
}
